import { useRef, type ReactNode } from 'react';
import { Monitor, Smartphone } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Button from '@/components/ui/Button';
import Dialog from '@/components/ui/Dialog';
import { useDeviceMode } from '@/contexts/DeviceModeContext';
import { formatClientVersionLocal } from '@/features/clientVersion/clientVersionTime';
import { useClientVersionNotice } from '@/features/clientVersion/useClientVersionNotice';

function ReloadStep({ icon, label, action }: { icon: ReactNode; label: string; action: string }) {
  return (
    <li className="flex items-start gap-3 rounded-control border border-edge-subtle bg-inset px-3 py-2.5">
      <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-control border border-edge-subtle bg-raised text-secondary">
        {icon}
      </span>
      <span className="min-w-0 pt-0.5">
        <span className="block font-ui text-small font-semibold text-primary">{label}</span>
        <span className="mt-0.5 block font-ui text-small text-secondary">{action}</span>
      </span>
    </li>
  );
}

/** Centered reload notice for the mobile surface. Hidden on kiosk and until a version check asks for it. */
export default function ClientVersionBanner() {
  const { t, i18n } = useTranslation();
  const { visible, updatedAt, dismiss } = useClientVersionNotice();
  const deviceMode = useDeviceMode();
  const dismissRef = useRef<HTMLButtonElement>(null);
  const updatedLocal = updatedAt ? formatClientVersionLocal(updatedAt, i18n.language) : null;

  return (
    <Dialog
      open={visible}
      onClose={dismiss}
      placement="center"
      size="sm"
      scrollBody={false}
      closeOnBackdrop={false}
      closeLabel={t('common.close')}
      title={t('clientVersion.title')}
      description={updatedLocal ? t('clientVersion.updated', { time: updatedLocal }) : undefined}
      initialFocus={dismissRef}
      footer={
        <Button ref={dismissRef} type="button" variant="primary" onClick={dismiss}>
          {t('common.dismiss')}
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          <ReloadStep
            icon={<Monitor className="size-4" aria-hidden />}
            label={t('clientVersion.computerLabel')}
            action={t('clientVersion.computerAction')}
          />
          <ReloadStep
            icon={<Smartphone className="size-4" aria-hidden />}
            label={t('clientVersion.phoneLabel')}
            action={t('clientVersion.phoneAction')}
          />
        </ul>
        <p className="m-0 font-ui text-small text-secondary">
          {t(deviceMode === 'mobile' ? 'clientVersion.leadPhone' : 'clientVersion.lead')}
        </p>
      </div>
    </Dialog>
  );
}
